// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import styles from './HistoryRawMaterials.module.scss';
import { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeNumber, mergeClasses } from 'util/commons';
import ResponsiveHistoryTable, {
  CustomColumn,
} from 'components/molecules/ResponsiveHistoryTable';
import CustomPagination from 'components/atoms/CustomPagination';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import IconCalendarTablet from 'components/atoms/icons/IconCalendarTablet';
import { IconEdit } from 'components/atoms/icons/IconEdit';
import {
  useFetchRawMaterialHistoryQuery,
  useUpdateSubSiteResourceMutation,
} from 'apis/compressions_user/compressionUserApi';
import { BookingQRType, RawMaterialStatus } from 'util/Enums';
import { toast } from 'react-toastify';
import { handleError, isError } from 'util/errorHandler';
import { getFormatDateWithDeviceTimezone } from 'ts/formatDate';
import { useSelector } from 'react-redux';
import { selectLoading } from 'redux/slices/loadingSlice';
import WeightInput from './WeightInput';
import QRCode from 'react-qr-code';
import Portal from 'components/atoms/Portal';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const PRINT_WIDTH = 640;

type WeightEditCellProps = {
  initialWeight: string;
  onChange: (weight: string) => void;
  className: string;
  inputClassName: string;
  onCancel?: () => void;
  onSubmit?: (weight: string) => void;
};

function WeightEditCell({
  initialWeight,
  onChange,
  className,
  inputClassName,
  onCancel,
  onSubmit,
}: WeightEditCellProps) {
  const [value, setValue] = useState(initialWeight);

  return (
    <div className={className}>
      <WeightInput
        className={inputClassName}
        value={value}
        onChange={(nextValue) => {
          setValue(nextValue);
          onChange(nextValue);
        }}
        autoFocus
        onSubmit={onSubmit}
        onCancel={onCancel}
      />
      <span>kg</span>
    </div>
  );
}

const PAGE_SIZE = 20;

export type RawMaterialItem = {
  id: string;
  receivedDatetime: string;
  place: string;
  materialName: string;
  itemName?: string;
  weight: string;
  measuredWeight?: string;
  containerWeight?: string;
  status: RawMaterialStatus;
  inventoryWeight?: string;
  type: string;
  editable: boolean;
  editingWeight?: string;
  qrCode?: string | null;
  grade?: { id: string; name: string } | null;
};

export const HistoryRawMaterials = ({
  onAppliedDateRangeChange,
}: {
  onAppliedDateRangeChange?: (range: [string, string] | undefined) => void;
}) => {
  const { t, i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [dateRange, setDateRange] = useState<[string, string]>();
  const [appliedDateRange, setAppliedDateRange] = useState<[string, string]>();

  const { data, refetch } = useFetchRawMaterialHistoryQuery({
    page: currentPage + 1,
    take: PAGE_SIZE,
    dateFrom: appliedDateRange?.[0],
    dateTo: appliedDateRange?.[1],
  });

  const [displayRows, setDisplayRows] = useState<RawMaterialItem[]>([]);
  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);
  const { isLoading } = useSelector(selectLoading);
  const [activeQrRow, setActiveQrRow] = useState<RawMaterialItem | null>(null);
  const qrModalRef = useRef<HTMLDivElement>(null);
  const qrModalContentRef = useRef<HTMLDivElement>(null);

  const handleQrModalClick = (e: React.MouseEvent) => {
    if (qrModalRef.current && e.target === qrModalRef.current) {
      setActiveQrRow(null);
    }
  };

  const isAndroid = () => /Android/i.test(navigator.userAgent);

  const handlePrintQr = () => {
    if (!qrModalContentRef.current || !activeQrRow) {
      toast.error(t('history_emission_card.pdf_export_error'));
      return;
    }
    if (isAndroid()) {
      window?.printSubEmissionLabel?.(
        JSON.stringify({
          id: activeQrRow.id,
          qrCode: activeQrRow.qrCode,
          weight: normalizeNumber({ value: activeQrRow.weight }),
          measuredWeight: normalizeNumber({
            value: activeQrRow.measuredWeight,
          }),
          containerWeight: normalizeNumber({
            value: activeQrRow.containerWeight,
          }),
          resourceId: activeQrRow.id,
          materialName: activeQrRow.materialName,
          itemName: activeQrRow.itemName,
          place: activeQrRow.place,
          registDatetime: getFormatDateWithDeviceTimezone(
            activeQrRow.receivedDatetime
          ),
          grade: activeQrRow.grade,
        }),
        i18n.language
      );
      setActiveQrRow(null);
      return;
    }
    try {
      const cloned = qrModalContentRef.current.cloneNode(true) as HTMLElement;
      cloned.style.backgroundColor = '#FFFFFF';
      cloned.querySelectorAll('span, div, p').forEach((el) => {
        (el as HTMLElement).style.color = '#000000';
      });
      const button = cloned.querySelector('button');
      if (button) button.remove();
      cloned.style.position = 'absolute';
      cloned.style.left = '-9999px';
      document.body.appendChild(cloned);
      const exportHeight = Math.max(
        PRINT_WIDTH * Math.sqrt(2),
        cloned.scrollHeight
      );
      html2canvas(cloned, {
        backgroundColor: '#FFFFFF',
        width: PRINT_WIDTH,
        height: exportHeight,
        windowWidth: PRINT_WIDTH,
        windowHeight: exportHeight,
        scale: 2,
      })
        .then((canvas) => {
          if (document.body.contains(cloned)) document.body.removeChild(cloned);
          const pdf = new jsPDF('portrait', 'px', [
            canvas.width,
            canvas.height,
          ]);
          pdf.addImage(
            canvas.toDataURL('image/png'),
            'PNG',
            0,
            0,
            canvas.width,
            canvas.height
          );
          pdf.save(`raw-material-qr-${activeQrRow.id}.pdf`);
          toast.success(t('history_emission_card.pdf_export_success'));
          setActiveQrRow(null);
        })
        .catch(() => {
          if (document.body.contains(cloned)) document.body.removeChild(cloned);
          toast.error(t('history_emission_card.pdf_export_error'));
        });
    } catch {
      toast.error(t('history_emission_card.pdf_export_error'));
    }
  };

  useEffect(() => {
    setDisplayRows(data?.data ?? []);
  }, [data?.data]);

  const handleSearch = () => {
    const isSameRange =
      dateRange?.[0] === appliedDateRange?.[0] &&
      dateRange?.[1] === appliedDateRange?.[1];

    setCurrentPage(0);
    setAppliedDateRange(dateRange);
    onAppliedDateRangeChange?.(dateRange);

    if (isSameRange) {
      refetch();
    }
  };

  const [updateSubSiteResource] = useUpdateSubSiteResourceMutation();

  const [editingId, setEditingId] = useState<string | null>(null);

  const handleEdit = (row: RawMaterialItem) => {
    setDisplayRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, editingWeight: r.weight } : r))
    );
    setEditingId(row.id);
  };

  const handleSave = (id: string, weight: string) => {
    if (!weight || isLoading) {
      return;
    }

    updateSubSiteResource({ id, weight })
      .then((result) => {
        if (isError(result)) return;
        toast.success(t('messages.M_011'));
        refetch();
        setEditingId(null);
        setDisplayRows((prev) =>
          prev.map((r) =>
            r.id === id ? { ...r, weight, editingWeight: undefined } : r
          )
        );
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const columns: CustomColumn<RawMaterialItem>[] = useMemo(
    () => [
      {
        Header: t('histories.label_received_registered_date_time') as string,
        accessor: (row) => row.receivedDatetime,
        Cell: ({ value }) => <>{getFormatDateWithDeviceTimezone(value)}</>,
      },
      {
        Header: t('common_history_field.place') as string,
        accessor: (row) => row.place,
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('common_history_field.material_name') as string,
        accessor: (row) => row.materialName,
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('histories.label_item_name') as string,
        accessor: (row) => row.itemName || '-',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('common_history_field.weight') as string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        accessor: 'weight' as any,
        Cell: ({ row }) =>
          editingId === row.original.id ? (
            <WeightEditCell
              initialWeight={row.original.editingWeight || row.original.weight}
              onChange={(w) => {
                setDisplayRows((prev) =>
                  prev.map((r) =>
                    r.id === row.original.id ? { ...r, editingWeight: w } : r
                  )
                );
              }}
              className={styles.weightEditCell}
              inputClassName={styles.weightInput}
              onCancel={() => {
                setEditingId(null);
                setDisplayRows((prev) =>
                  prev.map((r) =>
                    r.id === row.original.id
                      ? { ...r, editingWeight: undefined }
                      : r
                  )
                );
              }}
              onSubmit={() =>
                handleSave(row.original.id, row.original.editingWeight || '')
              }
            />
          ) : (
            <>{normalizeNumber({ value: row.original.weight }) + 'kg'}</>
          ),
      },
      {
        Header: t('histories.label_resource_id') as string,
        accessor: (row) => row.id,
        Cell: ({ value }) => <>{value || '-'}</>,
      },
      {
        Header: t('histories.label_status') as string,
        accessor: (row) => row.status,
        Cell: ({ value }) => (
          <>{t(`histories.raw_material_status.${value}`, value) || '-'}</>
        ),
      },
      {
        Header: t('histories.label_inventory') as string,
        accessor: (row) => row.inventoryWeight,
        Cell: ({ value }) => <>{normalizeNumber({ value }) || '-'}</>,
      },
      {
        Header: 'QR',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        accessor: 'qrCode' as any,
        className: styles.centerColumn,
        Cell: ({ row }) =>
          row.original.qrCode &&
          row.original.type === BookingQRType.Resource ? (
            <div className={styles.printLabelButton}>
              <button
                type="button"
                onClick={() => setActiveQrRow(row.original)}
              >
                {t('history_emission_card.card_print_qr_label_button')}
              </button>
            </div>
          ) : (
            <>-</>
          ),
      },
      {
        Header: t('histories.label_action') as string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        accessor: 'actions' as any,
        className: styles.centerColumn,
        Cell: ({ row }) => {
          const isEditing = editingId === row.original.id;

          if (!row.original.editable) {
            return (
              <div className={styles.actionCell}>
                <IconEdit stroke="#6F7175" />
              </div>
            );
          }

          if (isEditing) {
            return (
              <button
                className={styles.saveButton}
                onClick={() =>
                  handleSave(row.original.id, row.original.editingWeight || '')
                }
                disabled={!row.original.editingWeight}
              >
                {t('common.button.save')}
              </button>
            );
          }

          return (
            <div
              className={mergeClasses(styles.actionCell, {
                [styles.actionCellEditable]: row.original.editable,
              })}
              onClick={() => handleEdit(row.original)}
            >
              <IconEdit stroke="#86EA5C" />
            </div>
          );
        },
      },
    ],
    [t, editingId, isLoading]
  );

  return (
    <div className={styles.historyRawMaterials}>
      <div className={styles.header}>
        <TableFilterCalendar
          wrapperClassName={styles.dateFilter}
          containerClassName={styles.dateFilterInner}
          expanded={{
            placeholder: t('common.placeholder.select_date_range'),
            icon: <IconCalendarTablet />,
          }}
          portalPositionPoint={(ref) => ref}
          endDateRequired
          value={dateRange}
          onChange={setDateRange}
        />
        <button className={styles.searchButton} onClick={handleSearch}>
          {t('common.placeholder.search')}
        </button>
      </div>

      <div className={styles.content}>
        <ResponsiveHistoryTable<RawMaterialItem>
          data={displayRows}
          columns={columns}
          scrollResetDependencies={[currentPage]}
        />
      </div>

      {totalPages > 0 && (
        <div className={styles.paginationContainer}>
          <CustomPagination
            pageCount={totalPages}
            onPageChange={({ selected }) => setCurrentPage(selected)}
            forcePage={currentPage}
          />
        </div>
      )}

      {activeQrRow && activeQrRow.qrCode && (
        <Portal>
          <div
            className={styles.qrModal}
            ref={qrModalRef}
            onClick={handleQrModalClick}
          >
            <div ref={qrModalContentRef} className={styles.qrModalContent}>
              <div className={styles.qrModalMainContainer}>
                <div className={styles.qrCodeContainer}>
                  <div className={styles.qrCodeBackground}>
                    <QRCode
                      value={activeQrRow.qrCode}
                      size={200}
                      className={styles.qrCodeStyle}
                    />
                  </div>
                </div>
                <div className={styles.qrModalRightContent}>
                  <div className={styles.qrModalInfoRowWeight}>
                    <span className={styles.qrModalLabelWeight}>
                      {t('common_history_field.weight')}
                    </span>
                    <span className={styles.qrModalValueWeight}>
                      {normalizeNumber({ value: activeQrRow.weight })} kg
                    </span>
                  </div>
                  <div className={styles.qrModalInfoRow}>
                    <span className={styles.qrModalLabel}>
                      {t('histories.label_resource_id')}
                    </span>
                    <span className={styles.qrModalValue}>
                      {activeQrRow.id}
                    </span>
                  </div>
                  <div className={styles.qrModalInfoRow}>
                    <span className={styles.qrModalLabel}>
                      {t('histories.label_received_registered_date_time')}
                    </span>
                    <span className={styles.qrModalValue}>
                      {getFormatDateWithDeviceTimezone(
                        activeQrRow.receivedDatetime
                      )}
                    </span>
                  </div>
                  <div className={styles.qrModalInfoRow}>
                    <span className={styles.qrModalLabel}>
                      {t('common_history_field.place')}
                    </span>
                    <span className={styles.qrModalValue}>
                      {activeQrRow.place}
                    </span>
                  </div>
                  <div className={styles.qrModalInfoRow}>
                    <span className={styles.qrModalLabel}>
                      {t('common_history_field.material_name')}
                    </span>
                    <span className={styles.qrModalValue}>
                      {activeQrRow.materialName}
                    </span>
                  </div>
                  {activeQrRow.itemName && (
                    <div className={styles.qrModalInfoRow}>
                      <span className={styles.qrModalLabel}>
                        {t('histories.label_item_name')}
                      </span>
                      <span className={styles.qrModalValue}>
                        {activeQrRow.itemName}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={handlePrintQr}
                className={styles.qrModalPrintButton}
              >
                {t('history_emission_card.print_label_button')}
              </button>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};
